import { NotificationItemGroup } from '../models/notification-item-group';
import { Observable, of } from 'rxjs';
import { NotificationItem } from '../models/notification-item';
import { AggregatedNotificationService } from '../services/aggregated-notification-service';
import { NotificationGroupUpdate } from '../interfaces/notification-group-update';
import { BehaviorSubject } from 'rxjs';
import {ActivityFeedNotificationItem} from '../models/activity-feed-notification-item';
import {pdsUtils} from '../pds-utils';
import {CommunicationNotificationItemGroup} from '../models/communication-notification-item-group';
import {CommunicationNotificationItem} from '../models/communication-notification-item';
import {NotificationService} from '../services/notification-service';
import { AssignmentNotificationItemGroup } from '../models/assignment-notification-item-group';

//See UI Docs components/notification-indicator for more info on the usage and additional documentation
export class NotificationIndicatorController {

  notificationService: AggregatedNotificationService;

  notificationItemGroups: Array<NotificationItemGroup>;

  notificationItemGroupStream: BehaviorSubject<NotificationGroupUpdate> = new BehaviorSubject<NotificationGroupUpdate>({
    notificationGroups: [],
    totalUnreadNotificationCount: 0,
    currentTabUnreadCount: 0,
    notificationTypeUnreadCountMap: {}
  });

  notificationGroupMap: { [id: string]: NotificationItemGroup } = {};
  lookUpKeyToNotificationGroupMap: { [aggregationKey: string]: NotificationItemGroup } = {};

  currentNotificationType: string;

  constructor(notificationService: AggregatedNotificationService, private notificationsGroupsToDisplay = 100) {

    this.notificationService = notificationService;
    this.currentNotificationType = ActivityFeedNotificationItem.NOTIFICATION_TYPE;  // default
    //We want to sign up to listen for new notifications.

    //We want to first download the unseen notifications
    this.notificationService.getExistingNotifications().subscribe((notifications: Array<NotificationItem>) => {
      this.notificationItemGroups = [];

      for (let notification of notifications) {
        this.addNotification(notification, true);
      }
      this.fireNotificationStream();

      this.setupNotificationListener();
    });
  }

  setCurrentNotificationType(notificationType: string): void {
    this.currentNotificationType = notificationType;
    this.fireNotificationStream(false);
  }

  setupNotificationListener(): void {
    this.notificationService.initializeNotificationStream().subscribe(() => {
      //This observable fires many times.
      this.notificationService.notificationStream.subscribe((notification: NotificationItem) => {
        //We have a new Notification!
        //We need to add it to the correct set.
        this.addNotification(notification);
      });
    });
  }

  /**
   * Takes NotificationItems and adds them to a NotificationItemGroup. NotificationItemGroup is chosen
   * based on the aggregationKey of the NotificationItem. If there are no NotificationItemGroups with a
   * matching aggregationKey to the NotificationItem, we create a new NotificationItemGroup and add the
   * NotificationItem to the new Group.
   * @param notification NotificationItem to be added to the notification
   * @param suppressUpdates flag indicating whether or not to update unread notification count on NotificationIndicator
   */
  addNotification(notification: NotificationItem, suppressUpdates = false): void {
    let selectedNotificationItemGroup: NotificationItemGroup | undefined;

    selectedNotificationItemGroup = this.notificationItemGroups.find((notificationItemGroup: NotificationItemGroup) => {
      return notificationItemGroup.aggregationKey === notification.getAggregationKey();
    });

    if (selectedNotificationItemGroup != null) {
      //The set already existed
      selectedNotificationItemGroup.addNotification(notification);
    } else {
      //The set did not already exist
      switch (notification.type) {
        case 'COMMUNICATION_NOTIFICATION':
          selectedNotificationItemGroup = new CommunicationNotificationItemGroup(
            notification.type,
            notification.getAggregationKey(),
            (<CommunicationNotificationItem>notification).getUserType(),
            (<CommunicationNotificationItem>notification).currentUserUid
          );
          break;
        case 'ASSIGNMENT_NOTIFICATION':
          selectedNotificationItemGroup = new AssignmentNotificationItemGroup(
            notification.type,
            notification.getAggregationKey(),
            notification.isMarkedReadOnClick,
            notification.subtype
          );
          break;
        default:
          selectedNotificationItemGroup = new NotificationItemGroup(
            notification.type,
            notification.getAggregationKey(),
            notification.isMarkedReadOnClick
          );
          break;
      }
      selectedNotificationItemGroup.addNotification(notification);
      this.notificationGroupMap[selectedNotificationItemGroup.id] = selectedNotificationItemGroup;
      this.lookUpKeyToNotificationGroupMap[selectedNotificationItemGroup.lookUpKey] = selectedNotificationItemGroup;
      this.notificationItemGroups.unshift(selectedNotificationItemGroup);
    }

    if (!suppressUpdates) {
      this.fireNotificationStream();
    }
  }

  //Fire observable triggering NotificationIndicator to update unread notification count.
  fireNotificationStream(updateCurrentNotificationType = true): void {
    let unreadCounts = this.getNotificationTypeUnreadCountMap();

    let totalUnread: number = this.notificationItemGroups.reduce((prev: number, next: NotificationItemGroup) => {
      return prev + next.unreadNotificationCount;
    }, 0);

    // notification area should default to the first type that has notifications if the current type does not have any
    if (updateCurrentNotificationType && (unreadCounts[this.currentNotificationType] == null || unreadCounts[this.currentNotificationType] === 0)) {
      let firstServiceWithNotifications = this.notificationService.notificationServices.find((service: NotificationService) => {
        return unreadCounts[service.type] > 0;
      });

      this.currentNotificationType = firstServiceWithNotifications != null ? firstServiceWithNotifications.type : this.currentNotificationType;
    }

    let filteredGroups = this.notificationItemGroups.filter((group) => {
      return group.type === this.currentNotificationType;
    });

    this.notificationItemGroupStream.next({
      notificationGroups: filteredGroups.slice(0, this.notificationsGroupsToDisplay),
      totalUnreadNotificationCount: totalUnread,
      currentTabUnreadCount: unreadCounts[this.currentNotificationType] || 0,
      notificationTypeUnreadCountMap: this.getNotificationTypeUnreadCountMap()
    });
  }

  /**
   * Marks a singular NotificationItemGroup as read and triggers NotificationIndicator
   * to update unread NotificationItem count.
   * @param group NotificationItemGroup to be marked as read.
   */
  markGroupAsRead(group: NotificationItemGroup): void {
    let controllerGroup = this.notificationGroupMap[group.id];
    if (controllerGroup == null) {
      throw 'bad notification mapping (read)';
    }
    this.notificationService.markAsRead(controllerGroup.getUnreadNotifications()).subscribe(() => {
      controllerGroup.markAsRead();
      this.fireNotificationStream();
    });
  }

  // Marks all NotificationItemGroups as seen. Currently marking notificationItemGroups as seen has no purpose.
  markAllAsSeen(): void {
    let items: Array<NotificationItem> = [];
    for (let notificationGroup of this.notificationItemGroups || []) {
      if (!notificationGroup.isSeen && notificationGroup.type === this.currentNotificationType) {
        notificationGroup.markAsSeen();
        items.push(...notificationGroup.notifications);
      }
    }

    this.notificationService.markAsSeen(items).subscribe(() => {
      this.fireNotificationStream();
    });
  }

  // Marks all NotificationItemGroups in the current tab as read and triggers NotificationIndicator to update unread count.
  markAllAsRead(): void {
    let items: Array<NotificationItem> = [];

    for (let notificationGroup of this.notificationItemGroups || []) {
      if (!notificationGroup.isRead && notificationGroup.type === this.currentNotificationType) {
        items.push(...notificationGroup.getUnreadNotifications());
        notificationGroup.markAsRead();
      }
    }

    this.notificationService.markAsRead(items).subscribe(() => {
      this.fireNotificationStream();
    });
  }

  getNotificationTypeUnreadCountMap(): { [notificationType: string]: number } {
    let notificationCountTypeMap = {};
    if (this.notificationItemGroups != null) {
      let groups = pdsUtils.groupBy(this.notificationItemGroups, group => group.type);
      for (let notificationType of Object.keys(groups)) {
        notificationCountTypeMap[notificationType] = groups[notificationType]
          .reduce((prev: number, itemGroup: NotificationItemGroup) => {
            return prev + itemGroup.unreadNotificationCount;
          }, 0);
      }
    }
    return notificationCountTypeMap;
  }

  /*
    This method will take a lookup key an use it to look up a notification item group. It will mark each
    notification item in the group read.

    The persist update flag is available because there are cases where the caller of this method will have already
    persisted the change and cases where the caller hasn't persisted the change or can't. (avoid extra service calls)
   */
  markGroupReadByLookUpKey(lookUpKey: string, persistUpdate = false): void {
    let group = this.lookUpKeyToNotificationGroupMap[lookUpKey];

    if (group != null && persistUpdate) {
      this.markGroupAsRead(group);
    } else if (group != null) {
      group.markAsRead();
      this.fireNotificationStream(); // this is called in this.markGroupAsRead so we only need to call in this case.
    }
  }

  /*
    This method provides the same functionality as markGroupReadByLookUpKey except that it allows you to also provide
    a lookUpFunction that can be used to filter the notifications that are getting marked read.
   */
  markReadByLookUpKeyAndFunction(lookUpKey: string, lookUpFunction: (item: NotificationItem) => boolean, persistUpdate = false): void {
    let group = this.lookUpKeyToNotificationGroupMap[lookUpKey];
    if (group == null) {
      return;
    }

    let itemsToMark = group.markReadWhere(lookUpFunction);
    let persistObs: Observable<any> = of(true);

    if (persistUpdate) {
      persistObs = this.notificationService.markAsRead(itemsToMark);
    }

    persistObs.subscribe(() => this.fireNotificationStream());
  }
}
